import { Trans, useTranslation } from 'react-i18next';
import { Button, Modal } from '@/components/ui';
import type { ScopedAccountsVM } from './useScopedAccounts';
import type { ScopedAccountWritesVM } from './useScopedAccountWrites';

/**
 * EAA-1839: the three single-row dialogs of the scoped accounts browser —
 * "+ Tài khoản" (grant an existing account into this đơn vị), the revoke confirm
 * and the reset-password confirm — plus the one-time temp-password panel.
 *
 * EAA-1839 (frame 35 completion pass): the "+ Tài khoản" grant modal that used to
 * live here IS GONE, replaced by `ProvisionAccountModal`'s `existing` mode (frame
 * 35f). It is worth saying why, because the old modal was not wrong — it was
 * narrower. It asked the operator to TYPE a `user_sub`, because when it was written
 * there was no way to look one up; frames 35e/35f exist precisely because
 * `GET /accounts?search=` landed and a sub can now be chosen from a list that also
 * shows the person's current đơn vị and Level. Keeping both would have left two
 * "add an account to this đơn vị" entry points writing through different endpoints
 * (`POST .../assignments` vs `bulk-apply`), which is how the two account screens
 * drifted apart in the first place.
 *
 * The still-real observation from that modal, preserved: creating a Keycloak
 * identity (`POST /accounts`) produces NO `Assignment`, so an account created alone
 * never appears in this list — which is why frame 35e's create mode does both
 * writes. See `useProvisionAccount`.
 */
export function ScopedAccountDialogs({
    vm,
    writes,
}: {
    vm: ScopedAccountsVM;
    writes: ScopedAccountWritesVM;
}) {
    const { t } = useTranslation();
    const revokeTarget = writes.revokeTarget;
    const resetTarget = writes.resetTarget;
    const revokeIdentity = revokeTarget?.user_email || revokeTarget?.user_sub;
    const resetIdentity = resetTarget?.user_email || resetTarget?.user_sub;

    return (
        <>
            <Modal
                open={Boolean(revokeTarget)}
                onCancel={() => writes.setRevokeTarget(null)}
                title={t('console.hierarchy.scopedAccounts.dialogs.revokeTitle')}
                footer={
                    <>
                        <Button
                            onClick={() => writes.setRevokeTarget(null)}
                            disabled={writes.revokeBusy}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            danger
                            onClick={() => {
                                if (revokeTarget) writes.runRevoke(revokeTarget);
                            }}
                            disabled={writes.revokeBusy}
                        >
                            {writes.revokeBusy
                                ? t('console.hierarchy.scopedAccounts.dialogs.revokeBusy')
                                : t('console.hierarchy.scopedAccounts.dialogs.revokeButton')}
                        </Button>
                    </>
                }
            >
                <p className="portal-confirm-message">
                    {t('console.hierarchy.scopedAccounts.dialogs.revokeConfirmMessage', {
                        identity: revokeIdentity,
                    })}
                </p>
            </Modal>

            {vm.canReset && (
                <Modal
                    open={Boolean(resetTarget)}
                    onCancel={() => writes.setResetTarget(null)}
                    title={t('console.hierarchy.scopedAccounts.dialogs.resetTitle')}
                    footer={
                        <>
                            <Button
                                onClick={() => writes.setResetTarget(null)}
                                disabled={writes.resetBusy}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => {
                                    if (resetTarget) writes.runReset(resetTarget);
                                }}
                                disabled={writes.resetBusy}
                            >
                                {writes.resetBusy
                                    ? t('console.hierarchy.scopedAccounts.dialogs.resetBusy')
                                    : t('console.hierarchy.scopedAccounts.dialogs.resetButton')}
                            </Button>
                        </>
                    }
                >
                    <p className="portal-admin-hint">
                        <Trans
                            i18nKey="console.hierarchy.scopedAccounts.dialogs.resetHint"
                            values={{ identity: resetIdentity }}
                            components={{ bold: <strong /> }}
                        />
                    </p>
                    <p className="portal-accounts-onetime-warning">
                        {t('console.hierarchy.scopedAccounts.dialogs.resetOneTimeWarning')}
                    </p>
                </Modal>
            )}

            {writes.resetResult && (
                <div className="portal-admin-secret" role="alert">
                    <strong>{t('console.hierarchy.scopedAccounts.dialogs.resetResultHeading')}</strong>
                    <span>
                        {t('console.hierarchy.scopedAccounts.dialogs.usernameLabel')}{' '}
                        <code>{writes.resetResult.username}</code>
                    </span>
                    <span>
                        {t('console.hierarchy.scopedAccounts.dialogs.tempPasswordLabel')}{' '}
                        <code>{writes.resetResult.temp_password}</code>
                    </span>
                    {/* EAA-1804: this had no className, which is exactly why the earlier pass
                        skipped it — and why that was wrong. `.portal-admin-secret button`
                        (one class + one element) paints the legacy portal-btn skin on ANY
                        bare button in this container, so it rendered as a portal-btn without
                        ever saying so. That selector is now scoped `:not(.ant-btn)`, so the
                        kit Button takes over cleanly. */}
                    <Button onClick={writes.dismissResetResult}>
                        {t('console.hierarchy.scopedAccounts.dialogs.dismissResetResult')}
                    </Button>
                </div>
            )}
        </>
    );
}
